import { useCallback, useEffect, useState } from "react";

import { Button, ConfirmDialog, DisclosureRow, Field, Money, Notice, Status, Surface, Textarea } from "../../../../components/ui";
import CorrectionDetail from "./CorrectionDetail";
import { adjustmentsApi, classificationLabel, myStateOf } from "./adjustmentView";

// Report 54B (D2): the corrections Finance approved on one of my payslips, in My HR. The employee sees why, what
// changed and what is outstanding; confirms cash they received; and can acknowledge or dispute a claim on them.
// The employee never approves or edits the amount, and an acknowledgement is not a payment. The server takes the
// employee from the session.

export default function MyCorrections({ payslipId }) {
  const [items, setItems] = useState([]);
  const [failed, setFailed] = useState(false);
  const [dialog, setDialog] = useState(null); // { type, item }
  const [notice, setNotice] = useState("");
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    try {
      setItems(await adjustmentsApi.mine(payslipId ? { payslip_id: payslipId } : undefined));
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, [payslipId]);
  useEffect(() => { load(); }, [load]);

  if (failed) return <Notice tone="warning">Corrections to this payslip couldn't be loaded. The payslip above is still correct as issued.</Notice>;
  if (!items.length) return null;

  const done = (message) => async () => { setDialog(null); setReason(""); setNotice(message); await load(); };
  const item = dialog?.item;

  return (
    <section aria-label="Corrections to this payslip" className="space-y-3 print:hidden">
      <div>
        <h2 className="text-section-title text-foreground">Corrections to this payslip</h2>
        <p className="text-body-sm text-secondary">The payslip above stays as it was issued. A correction is recorded and paid separately.</p>
      </div>
      {notice ? <Notice tone="success" role="status">{notice}</Notice> : null}
      {items.map((row) => {
        const state = myStateOf(row);
        const pay = row.direction === "PAY_EMPLOYEE";
        const open = !pay && ["OPEN", "PARTIAL"].includes(row.settlementStatus);
        return (
          <Surface key={row.id} as="article" aria-label={`${classificationLabel(row.classification)} ${row.number}`}>
            <div className="space-y-3 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="text-panel-title text-foreground">{classificationLabel(row.classification)}</h3>
                  <p className="text-body-sm text-secondary">{row.reason}</p>
                </div>
                <div className="text-right">
                  <p className="text-meta text-secondary">{pay ? "Owed to you" : "Claimed from you"}</p>
                  <Money value={row.amountCents} className="text-money text-foreground" />
                </div>
              </div>
              <p className="text-body-sm">
                <Status tone={state.tone} icon={state.tone !== "neutral"} className="font-medium">{state.label}</Status>
                <span className="mt-0.5 block text-secondary">{state.detail}</span>
              </p>
              {!pay && row.disputeResolution ? <p className="text-body-sm text-secondary">Finance's answer to your dispute: {row.disputeResolution}</p> : null}
              <div className="flex flex-wrap gap-2 max-sm:flex-col">
                {pay && row.settlementStatus === "HANDED_OVER" ? <Button variant="primary" onClick={() => setDialog({ type: "confirm", item: row })}>Confirm cash received</Button> : null}
                {open && row.employeeResponse === "NONE" ? <Button onClick={() => setDialog({ type: "acknowledge", item: row })}>Acknowledge</Button> : null}
                {open && row.employeeResponse !== "DISPUTED" ? <Button onClick={() => setDialog({ type: "dispute", item: row })}>Dispute this claim</Button> : null}
              </div>
            </div>
            <div className="border-t border-subtle px-4">
              <DisclosureRow label="What changed and why" hint="The figures, the tax treatment, the money that moved and the history">
                <div className="pb-4"><CorrectionDetail item={row} audience="employee" /></div>
              </DisclosureRow>
            </div>
          </Surface>
        );
      })}

      {dialog?.type === "confirm" ? (
        <ConfirmDialog title="Confirm you received this cash?" confirmLabel="Confirm cash received"
          facts={[{ label: "Amount", value: <Money value={item.amountCents} /> }, { label: "For", value: classificationLabel(item.classification) }]}
          consequence="Confirm only once the cash is in your hands. This marks the correction as paid and cannot be undone."
          onConfirm={async () => { await adjustmentsApi.confirmReceipt(item.id); await done("You confirmed receiving the cash. The correction is now marked as paid.")(); }}
          onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.type === "acknowledge" ? (
        <ConfirmDialog title="Acknowledge this claim?" confirmLabel="Acknowledge claim"
          facts={[{ label: "Still outstanding", value: <Money value={item.outstandingCents} /> }]}
          consequence="This says you have seen the claim and do not dispute it. It is not a payment: the amount goes down only when money is really returned or taken with a paid salary."
          onConfirm={async () => { await adjustmentsApi.acknowledge(item.id); await done("You acknowledged the claim.")(); }}
          onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.type === "dispute" ? (
        <ConfirmDialog title="Dispute this claim?" confirmLabel="Send dispute" confirmDisabled={reason.trim().length < 10}
          facts={[{ label: "Still outstanding", value: <Money value={item.outstandingCents} /> }]}
          consequence="Finance must answer before anything more is recorded or taken. Money already returned or recovered stays recorded until Finance decides."
          onConfirm={async () => { await adjustmentsApi.dispute(item.id, reason.trim()); await done("Your dispute was sent to Finance.")(); }}
          onClose={() => { setDialog(null); setReason(""); }}>
          <Field label="Why you dispute it" hint="At least 10 characters. Finance reads this." required>
            <Textarea rows={3} maxLength={1000} value={reason} onChange={(event) => setReason(event.target.value)} />
          </Field>
        </ConfirmDialog>
      ) : null}
    </section>
  );
}
